import React, { useState, useCallback } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { ModuleAccessGuard } from '@/components/ModuleAccessGuard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDropzone } from 'react-dropzone';
import { useCustomers } from '@/hooks/useCustomers';
import { useSuppliers } from '@/hooks/useSuppliers';
import { useManualUpload } from '@/hooks/useBillsAPI';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Upload, X, FileText, Image, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface UploadedFile {
  file: File;
  preview?: string;
}

const ManualUploadBills = () => {
  const [customerId, setCustomerId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [parserType, setParserType] = useState<'automation' | 'manual'>('automation');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const { currentWorkspace } = useWorkspace();
  const { customers } = useCustomers();
  const { suppliers } = useSuppliers();
  const manualUpload = useManualUpload();
  const { toast } = useToast();
  const navigate = useNavigate();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const validFiles = acceptedFiles.filter((file) => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: `${file.name}: Only PDF, JPG, and PNG files are allowed`,
          variant: 'destructive',
        });
        return false;
      }

      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: `${file.name}: File size exceeds 10MB limit`,
          variant: 'destructive',
        });
        return false;
      }

      return true;
    });

    const newFiles = validFiles.map((file) => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
    }));

    setFiles((prev) => {
      const combined = [...prev, ...newFiles];
      if (combined.length > 50) {
        toast({
          title: 'Too many files',
          description: 'Maximum 50 files allowed',
          variant: 'destructive',
        });
        return combined.slice(0, 50);
      }
      return combined;
    });
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: 10 * 1024 * 1024,
  });

  const removeFile = (index: number) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleUpload = async () => {
    if (!customerId || !supplierId || files.length === 0) return;

    setUploading(true);
    try {
      const uploadedFiles = [];

      for (const { file } of files) {
        const fileName = `${Date.now()}-${file.name}`;
        const filePath = `bills/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('bills')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('bills')
          .getPublicUrl(filePath);

        uploadedFiles.push({
          file_url: urlData.publicUrl,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
        });
      }

      await manualUpload.mutateAsync({
        customer_id: customerId,
        supplier_id: supplierId,
        parser_type: parserType,
        files: uploadedFiles,
      });

      toast({
        title: 'Upload successful',
        description: `${files.length} file(s) uploaded and queued for processing`,
      });

      navigate(`/w/${currentWorkspace?.id}/dashboard/bills/unparsed`);
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const totalSize = files.reduce((acc, { file }) => acc + file.size, 0);
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <ModuleAccessGuard moduleId="bill_collection">
      <div className="container mx-auto py-6 space-y-6">
        <PageHeader
          title="Manual Bill Upload"
          description="Upload bills manually for processing"
        />

        <Card>
          <CardHeader>
            <CardTitle>Upload Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer">Customer *</Label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger id="customer">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers?.map((customer: any) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="supplier">Supplier *</Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger id="supplier">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers?.map((supplier: any) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Parser Type *</Label>
              <RadioGroup value={parserType} onValueChange={(v: any) => setParserType(v)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="automation" id="automation" />
                  <Label htmlFor="automation" className="font-normal">
                    Automation (AI-powered parsing)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="manual" id="manual" />
                  <Label htmlFor="manual" className="font-normal">
                    Manual (Queue for manual data entry)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>Upload Files</Label>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                {isDragActive ? (
                  <p className="text-primary">Drop files here...</p>
                ) : (
                  <>
                    <p className="text-muted-foreground mb-2">
                      Drag & drop files here, or click to select
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Accepts: PDF, JPG, PNG • Max: 10MB per file, 50 files total
                    </p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {files.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                Uploaded Files ({files.length}) - {formatSize(totalSize)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Sl.No</TableHead>
                    <TableHead className="w-16">Preview</TableHead>
                    <TableHead>File Name</TableHead>
                    <TableHead className="w-24">Size</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map(({ file, preview }, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        {preview ? (
                          <img
                            src={preview}
                            alt={file.name}
                            className="h-10 w-10 object-cover rounded"
                          />
                        ) : (
                          <FileText className="h-10 w-10 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{file.name}</TableCell>
                      <TableCell>{formatSize(file.size)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Button
                onClick={handleUpload}
                disabled={!customerId || !supplierId || files.length === 0 || uploading}
                className="w-full mt-4"
                size="lg"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading {files.length} file(s)...
                  </>
                ) : (
                  `Upload & Process ${files.length} file(s)`
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </ModuleAccessGuard>
  );
};

export default ManualUploadBills;
